import { el, clear } from '../../../core/dom.js';
import type { RescueModule, RescueResult } from '../../rescue-types.js';
import './breathing-orb.css';

interface Phase { label: string; from: number; to: number; ms: number }

const SCALE_MIN = 0.75;
const SCALE_MAX = 1.25;

const PHASES: Phase[] = [
  { label: 'Breathe in',  from: SCALE_MIN, to: SCALE_MAX, ms: 4000 },
  { label: 'Hold',        from: SCALE_MAX, to: SCALE_MAX, ms: 7000 },
  { label: 'Breathe out', from: SCALE_MAX, to: SCALE_MIN, ms: 8000 },
];

const SVG_NS = 'http://www.w3.org/2000/svg';
const RING_RADIUS = 174;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

// --- Audio tuning ---------------------------------------------------------
// Background green-noise bed: constant filter, very subtle volume swell.
const BG_PEAK_GAIN = 0.10;            // master volume of background noise
const BG_FLOOR_RATIO = 0.88;          // gain at exhale bottom (× BG_PEAK_GAIN)
const BG_CEIL_RATIO = 1.00;           // gain at inhale top (× BG_PEAK_GAIN)
const BG_GAIN_SMOOTH_SEC = 0.25;      // setTargetAtTime time constant
const BG_FILTER_FREQ_HZ = 528;        // green-noise band center, fixed
const BG_FILTER_Q = 0.6;              // bandpass Q

// Breath layer: envelope only fires on inhale/exhale phases.
const BREATH_PEAK_GAIN = 0.5;        // master volume of breath sound
const BREATH_EXHALE_GAIN_RATIO = 0.85;// exhale loudness vs inhale peak
const BREATH_GAIN_SMOOTH_SEC = 0.05;
const BREATH_FILTER_Q = 1.4;
const BREATH_INHALE_FREQ_START = 380; // bandpass freq at inhale start (Hz)
const BREATH_INHALE_FREQ_END = 900;   // bandpass freq at inhale end (Hz)
const BREATH_EXHALE_FREQ_START = 700; // bandpass freq at exhale start (Hz)
const BREATH_EXHALE_FREQ_END = 280;   // bandpass freq at exhale end (Hz)
const BREATH_FILTER_SMOOTH_SEC = 0.08;

function svg(tag: string, attrs: Record<string, string>): SVGElement {
  const node = document.createElementNS(SVG_NS, tag) as SVGElement;
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

export const breathingOrb: RescueModule = {
  id: 'breathing-orb',
  name: 'Just Breathe',
  kind: 'breathing',
  defaultDurationSec: 190,
  reducedMotion: true,
  safety: {
    gamblingLike: false,
    excludeForDomains: [],
    experimental: false,
    reducedMotionSafe: true,
  },
  async run(host, _ctx) {
    return new Promise<RescueResult>((resolve) => {
      clear(host);

      type WakeLockSentinelLike = { released: boolean; release: () => Promise<void> };
      type WakeLockNav = Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> } };
      let wakeLock: WakeLockSentinelLike | null = null;
      const requestWakeLock = async (): Promise<void> => {
        const wl = (navigator as WakeLockNav).wakeLock;
        if (!wl) return;
        try {
          wakeLock = await wl.request('screen');
        } catch {
          wakeLock = null;
        }
      };
      const releaseWakeLock = (): void => {
        const wl = wakeLock;
        wakeLock = null;
        if (wl && !wl.released) {
          wl.release().catch(() => {});
        }
      };
      const onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible' && !stopped) {
          void requestWakeLock();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      void requestWakeLock();
      document.body.classList.add('breath-active');

      type AudioCtxCtor = typeof AudioContext;
      type WebkitWindow = Window & { webkitAudioContext?: AudioCtxCtor };
      let audioCtx: AudioContext | null = null;
      let noiseGain: GainNode | null = null;
      let noiseSource: AudioBufferSourceNode | null = null;
      let breathGain: GainNode | null = null;
      let breathSource: AudioBufferSourceNode | null = null;
      let breathFilter: BiquadFilterNode | null = null;
      const startAudio = (): void => {
        const Ctor: AudioCtxCtor | undefined = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
        if (!Ctor) return;
        try {
          const ctxA = new Ctor();
          const sr = ctxA.sampleRate;
          const buf = ctxA.createBuffer(1, sr * 2, sr);
          const data = buf.getChannelData(0);
          let last = 0;
          for (let i = 0; i < data.length; i++) {
            const white = Math.random() * 2 - 1;
            last = (last + 0.02 * white) / 1.02;
            data[i] = last * 3.5;
          }
          const src = ctxA.createBufferSource();
          src.buffer = buf;
          src.loop = true;
          const filter = ctxA.createBiquadFilter();
          filter.type = 'bandpass';
          filter.frequency.value = BG_FILTER_FREQ_HZ;
          filter.Q.value = BG_FILTER_Q;
          const gain = ctxA.createGain();
          gain.gain.value = 0;
          src.connect(filter).connect(gain).connect(ctxA.destination);
          src.start();

          const bBuf = ctxA.createBuffer(1, sr * 2, sr);
          const bData = bBuf.getChannelData(0);
          for (let i = 0; i < bData.length; i++) bData[i] = Math.random() * 2 - 1;
          const bSrc = ctxA.createBufferSource();
          bSrc.buffer = bBuf;
          bSrc.loop = true;
          const bFilter = ctxA.createBiquadFilter();
          bFilter.type = 'bandpass';
          bFilter.frequency.value = BREATH_INHALE_FREQ_START;
          bFilter.Q.value = BREATH_FILTER_Q;
          const bGain = ctxA.createGain();
          bGain.gain.value = 0;
          bSrc.connect(bFilter).connect(bGain).connect(ctxA.destination);
          bSrc.start();

          audioCtx = ctxA;
          noiseSource = src;
          noiseGain = gain;
          breathSource = bSrc;
          breathFilter = bFilter;
          breathGain = bGain;
        } catch {
          audioCtx = null;
        }
      };
      let audioInitTried = false;
      const stopAudio = (): void => {
        const g = noiseGain;
        const s = noiseSource;
        const bg = breathGain;
        const bs = breathSource;
        const c = audioCtx;
        noiseGain = null;
        noiseSource = null;
        breathGain = null;
        breathSource = null;
        breathFilter = null;
        audioCtx = null;
        if (c) {
          try {
            if (g) { g.gain.cancelScheduledValues(c.currentTime); g.gain.setTargetAtTime(0, c.currentTime, 0.15); }
            if (bg) { bg.gain.cancelScheduledValues(c.currentTime); bg.gain.setTargetAtTime(0, c.currentTime, 0.15); }
          } catch { /* ignore */ }
        }
        setTimeout(() => {
          if (s) { try { s.stop(); } catch { /* ignore */ } }
          if (bs) { try { bs.stop(); } catch { /* ignore */ } }
          if (c) { c.close().catch(() => {}); }
        }, 600);
      };

      const stage = el('div', { class: 'rescue-stage' });
      const wrap = el('div', { class: 'breath-wrap' });

      const cue = el('div', { class: 'breath-cue' }, 'Breathe in');
      const orbStage = el('div', { class: 'breath-stage' });

      const WAVE_W = 1440;
      const WAVE_H = 360;
      const WAVE_OVERSHOOT = 800;
      const waves = svg('svg', { class: 'breath-waves', viewBox: `0 0 ${WAVE_W} ${WAVE_H}`, preserveAspectRatio: 'none' });
      const defs = svg('defs', {});
      const makeGlow = (id: string, offsetY: number): SVGElement => {
        const f = svg('filter', { id, x: '-15%', y: '-80%', width: '130%', height: '260%' });
        f.appendChild(svg('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: '9 22', result: 'wide' }));
        f.appendChild(svg('feOffset', { in: 'wide', dx: '0', dy: String(offsetY), result: 'wideOff' }));
        f.appendChild(svg('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: '4 9', result: 'mid' }));
        f.appendChild(svg('feOffset', { in: 'mid', dx: '0', dy: String(offsetY * 0.45), result: 'midOff' }));
        f.appendChild(svg('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: '1.6 2.4', result: 'inner' }));
        f.appendChild(svg('feOffset', { in: 'inner', dx: '0', dy: String(offsetY * 0.15), result: 'innerOff' }));
        f.appendChild(svg('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: '0.8', result: 'core' }));
        const m = svg('feMerge', {});
        m.appendChild(svg('feMergeNode', { in: 'wideOff' }));
        m.appendChild(svg('feMergeNode', { in: 'wideOff' }));
        m.appendChild(svg('feMergeNode', { in: 'midOff' }));
        m.appendChild(svg('feMergeNode', { in: 'innerOff' }));
        m.appendChild(svg('feMergeNode', { in: 'core' }));
        f.appendChild(m);
        return f;
      };
      defs.appendChild(makeGlow('breath-wave-glow-up', -22));
      defs.appendChild(makeGlow('breath-wave-glow-down', 22));
      waves.appendChild(defs);
      const wavePath = (d: string, cls: string): SVGElement => svg('path', { class: cls, d });
      const sineD = (amp: number, period: number, yMid: number, phase: number, _fillTo: 'top' | 'bottom'): string => {
        const pts: string[] = [];
        const step = 6;
        const endX = WAVE_W + WAVE_OVERSHOOT;
        for (let x = 0; x <= endX; x += step) {
          const y = yMid + amp * Math.sin(((x / period) * Math.PI * 2) + phase);
          pts.push(`${x === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`);
        }
        return pts.join(' ');
      };
      const yLow = WAVE_H * 0.58;
      const yHigh = WAVE_H * 0.34;
      const WAVE_PERIODS: readonly number[] = [720, 360, 240, 540, 380, 280];
      const WAVE_DIRS: readonly number[] = [-1, -1, 1, 1, -1, 1];
      const wavePaths: SVGElement[] = [
        wavePath(sineD(58, 720, yLow,  0.0, 'bottom'), 'breath-wave breath-wave-1'),
        wavePath(sineD(44, 360, yLow,  1.4, 'bottom'), 'breath-wave breath-wave-2'),
        wavePath(sineD(34, 240, yLow,  2.7, 'bottom'), 'breath-wave breath-wave-3'),
        wavePath(sineD(22, 540, yHigh, 0.6, 'top'),    'breath-wave breath-wave-4'),
        wavePath(sineD(20, 380, yHigh, 2.1, 'top'),    'breath-wave breath-wave-5'),
        wavePath(sineD(16, 280, yHigh, 3.8, 'top'),    'breath-wave breath-wave-6'),
      ];
      for (const p of wavePaths) waves.appendChild(p);
      const wavePhases: number[] = [0, 0, 0, 0, 0, 0];

      const halo = el('div', { class: 'breath-halo' });

      const progress = svg('svg', { class: 'breath-progress', viewBox: '0 0 360 360' });
      const track = svg('circle', {
        class: 'breath-progress-track',
        cx: '180', cy: '180', r: String(RING_RADIUS),
      });
      const fill = svg('circle', {
        class: 'breath-progress-fill',
        cx: '180', cy: '180', r: String(RING_RADIUS),
        'stroke-dasharray': String(RING_CIRC),
        'stroke-dashoffset': String(RING_CIRC),
      });
      progress.appendChild(track);
      progress.appendChild(fill);

      const goal = el('div', { class: 'breath-goal' });
      const orb = el('div', { class: 'breath-orb' });
      const count = el('div', { class: 'breath-count' }, '4');

      orbStage.appendChild(waves);
      orbStage.appendChild(halo);
      orbStage.appendChild(progress);
      orbStage.appendChild(orb);
      orbStage.appendChild(goal);
      orbStage.appendChild(count);

      const remain = el('div', { class: 'breath-remain' }, '');
      const stop = el('button', {
        class: 'btn ghost',
        onclick: () => endSession(),
      }, 'End early');
      const reduced = false;

      const AUDIO_PREF_KEY = 'rescue.breathing.audioMuted';
      const readMuted = (): boolean => {
        try { return localStorage.getItem(AUDIO_PREF_KEY) === '1'; } catch { return false; }
      };
      const writeMuted = (v: boolean): void => {
        try { localStorage.setItem(AUDIO_PREF_KEY, v ? '1' : '0'); } catch { /* ignore */ }
      };
      let muted = readMuted();
      const ICON_ON = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16 8c1.2 1 2 2.4 2 4s-.8 3-2 4"/></svg>';
      const ICON_OFF = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16 9l5 5M21 9l-5 5"/></svg>';
      const audioToggle = el('button', {
        class: 'breath-audio-toggle',
        type: 'button',
        attrs: {
          'aria-label': muted ? 'Unmute audio' : 'Mute audio',
          'aria-pressed': muted ? 'true' : 'false',
        },
        onclick: () => {
          muted = !muted;
          writeMuted(muted);
          audioToggle.innerHTML = muted ? ICON_OFF : ICON_ON;
          audioToggle.setAttribute('aria-label', muted ? 'Unmute audio' : 'Mute audio');
          audioToggle.setAttribute('aria-pressed', muted ? 'true' : 'false');
        },
      });
      audioToggle.innerHTML = muted ? ICON_OFF : ICON_ON;

      wrap.appendChild(cue);
      wrap.appendChild(orbStage);
      wrap.appendChild(remain);
      wrap.appendChild(stop);
      wrap.appendChild(audioToggle);
      stage.appendChild(wrap);
      host.appendChild(stage);

      const cycleLen = PHASES.reduce((s, p) => s + p.ms, 0);
      const GOAL_CYCLES = 10;
      const goalMs = GOAL_CYCLES * cycleLen;
      const startedAt = performance.now();

      const WAVE_DUR_START: [number, number, number, number, number, number] = [12, 15, 18, 22, 27, 33];
      const WAVE_DUR_END:   [number, number, number, number, number, number] = [32, 42, 52, 62, 74, 88];

      let lastPhaseIdx = -1;
      let rafId = 0;
      let stopped = false;
      let goalReached = false;
      let evaporating = false;
      let lastT = startedAt;
      let goalScale = SCALE_MAX;
      const GOAL_LERP_PER_SEC = 15;

      const loop = () => {
        if (stopped) return;
        const now = performance.now();
        const elapsedTotal = now - startedAt;
        const goalLeft = Math.max(0, goalMs - elapsedTotal);
        const totalFrac = Math.min(1, elapsedTotal / goalMs);
        const cyclesDone = Math.floor(elapsedTotal / cycleLen);
        if (!goalReached && cyclesDone >= GOAL_CYCLES) {
          goalReached = true;
          stop.textContent = 'End session';
          if (!reduced) triggerEvaporate();
        }

        const inCycle = elapsedTotal % cycleLen;
        let acc = 0;
        let idx = 0;
        for (let i = 0; i < PHASES.length; i++) {
          const p = PHASES[i];
          if (!p) continue;
          if (inCycle < acc + p.ms) { idx = i; break; }
          acc += p.ms;
        }
        const phase = PHASES[idx];
        if (!phase) return;
        const phaseElapsed = inCycle - acc;
        const phaseLeft = Math.max(0, phase.ms - phaseElapsed);

        if (idx !== lastPhaseIdx) {
          cue.textContent = phase.label;
          lastPhaseIdx = idx;
        }
        let scale = 1;
        if (reduced) {
          orb.style.transform = 'scale(1)';
          goal.style.transform = 'scale(1)';
          goal.style.opacity = '0';
          halo.style.transform = 'scale(1)';
          halo.style.opacity = '0.6';
        } else {
          const t = Math.min(1, phaseElapsed / phase.ms);
          const eased = easeInOut(t);
          scale = phase.from + (phase.to - phase.from) * eased;
          orb.style.transform = `scale(${scale.toFixed(4)})`;
          const dtSec = Math.min(0.1, (now - lastT) / 1000);
          const k = 1 - Math.exp(-GOAL_LERP_PER_SEC * dtSec);
          goalScale += (phase.to - goalScale) * k;
          goal.style.transform = `scale(${goalScale.toFixed(4)})`;
          const isHold = phase.from === phase.to;
          goal.style.opacity = isHold ? '0.3' : '0.85';
          halo.style.transform = `scale(${(scale * 1.15).toFixed(4)})`;
          halo.style.opacity = String(0.5 + 0.5 * ((scale - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)));
        }

        if (!audioInitTried) {
          audioInitTried = true;
          try { startAudio(); } catch { /* ignore */ }
        }
        try {
          if (audioCtx && noiseGain) {
            const norm = Math.max(0, Math.min(1, (scale - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)));
            const target = muted ? 0 : BG_PEAK_GAIN * (BG_FLOOR_RATIO + (BG_CEIL_RATIO - BG_FLOOR_RATIO) * norm);
            const ct = audioCtx.currentTime;
            noiseGain.gain.setTargetAtTime(target, ct, BG_GAIN_SMOOTH_SEC);

            if (breathGain && breathFilter) {
              const pt = Math.min(1, phaseElapsed / phase.ms);
              const isInhale = phase.label === 'Breathe in';
              const isExhale = phase.label === 'Breathe out';
              const env = Math.sin(Math.PI * pt);
              let bGainTarget = 0;
              let bFreq = BREATH_INHALE_FREQ_START;
              if (isInhale) {
                bGainTarget = BREATH_PEAK_GAIN * env;
                bFreq = BREATH_INHALE_FREQ_START + (BREATH_INHALE_FREQ_END - BREATH_INHALE_FREQ_START) * pt;
              } else if (isExhale) {
                bGainTarget = BREATH_PEAK_GAIN * BREATH_EXHALE_GAIN_RATIO * env;
                bFreq = BREATH_EXHALE_FREQ_START + (BREATH_EXHALE_FREQ_END - BREATH_EXHALE_FREQ_START) * pt;
              }
              if (muted) bGainTarget = 0;
              breathGain.gain.setTargetAtTime(bGainTarget, ct, BREATH_GAIN_SMOOTH_SEC);
              breathFilter.frequency.setTargetAtTime(bFreq, ct, BREATH_FILTER_SMOOTH_SEC);
            }
          }
        } catch { /* audio errors must not kill animation */ }

        const dt = Math.min(0.1, (now - lastT) / 1000);
        lastT = now;
        if (!reduced) {
          const waveProg = Math.min(1, elapsedTotal / goalMs);
          for (let i = 0; i < 6; i++) {
            const s = WAVE_DUR_START[i] ?? 20;
            const e = WAVE_DUR_END[i] ?? 50;
            const dur = s + (e - s) * waveProg;
            const period = WAVE_PERIODS[i] ?? 360;
            const dir = WAVE_DIRS[i] ?? -1;
            const velocity = (period / dur) * dir;
            const next = (wavePhases[i] ?? 0) + velocity * dt;
            const wrapped = ((next % period) + period) % period;
            wavePhases[i] = wrapped;
            const path = wavePaths[i];
            if (path) path.setAttribute('transform', `translate(${(-wrapped).toFixed(2)} 0)`);
          }
        }

        if (!evaporating) {
          fill.setAttribute('stroke-dashoffset', String(RING_CIRC * (1 - totalFrac)));
        }
        count.textContent = String(Math.ceil(phaseLeft / 1000));
        remain.textContent = goalReached
          ? `${cyclesDone} cycles`
          : `${Math.ceil(goalLeft / 1000)}s left`;

        rafId = requestAnimationFrame(loop);
      };
      rafId = requestAnimationFrame(loop);

      function triggerEvaporate() {
        if (evaporating) return;
        evaporating = true;
        orbStage.classList.add('breath-evaporating');
      }

      function endSession() {
        if (stopped) return;
        const sessionMs = performance.now() - startedAt;
        const kind: RescueResult['kind'] = goalReached ? 'completed' : 'abandoned';
        done({ kind, details: { totalMs: sessionMs } });
      }

      function done(res: RescueResult) {
        if (stopped) return;
        stopped = true;
        cancelAnimationFrame(rafId);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        releaseWakeLock();
        stopAudio();
        document.body.classList.remove('breath-active');
        resolve(res);
      }
    });
  },
};
